<template>
    <div class="min-h-full">
        <Disclosure
            v-slot="{ open, close }"
            as="nav"
            class="bg-gray-800"
        >
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="flex items-center justify-between h-16">
                    <div class="flex items-center">
                        <router-link
                            to="/"
                            class="text-white rounded-md focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                        >
                            <BrandMark />
                        </router-link>
                        <div
                            v-if="user != null"
                            class="hidden md:block"
                        >
                            <div class="ml-10 flex items-baseline space-x-4">
                                <router-link
                                    v-for="item in navigation"
                                    :key="item.name"
                                    :to="item.to"
                                    :class="[
                                        item.active ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                        'px-3 py-2 rounded-md text-sm font-medium',
                                    ]"
                                    :aria-current="item.active ? 'page' : undefined"
                                >
                                    {{ item.name }}
                                </router-link>
                            </div>
                        </div>
                    </div>
                    <div class="hidden md:block">
                        <div
                            v-if="user == null"
                            class="flex items-center gap-3"
                        >
                            <router-link
                                to="/login"
                                class="px-3 py-1.5 rounded-md text-sm font-medium text-white border border-gray-600 hover:bg-gray-700"
                            >
                                Sign in
                            </router-link>
                            <router-link
                                to="/signup"
                                class="btn-primary py-1.5"
                            >
                                Create account
                            </router-link>
                        </div>
                        <Menu
                            v-else
                            as="div"
                            class="ml-4 relative"
                        >
                            <MenuButton
                                class="max-w-xs bg-gray-800 rounded-full flex items-center text-sm focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                            >
                                <span class="sr-only">Open user menu</span>
                                <UserAvatar
                                    :user="user"
                                    class="h-8 w-8"
                                />
                            </MenuButton>
                            <transition
                                enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95"
                            >
                                <MenuItems
                                    class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg py-1 bg-white ring-1 ring-black/5 focus:outline-hidden"
                                >
                                    <div class="px-4 py-2 border-b border-gray-100">
                                        <div class="text-sm font-medium text-gray-900 truncate">{{ user.name }}</div>
                                        <div class="text-xs text-gray-500 truncate">{{ user.email }}</div>
                                    </div>
                                    <MenuItem v-slot="{ active }">
                                        <a
                                            href="#"
                                            :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']"
                                            @click.prevent="signout"
                                        >
                                            Sign out
                                        </a>
                                    </MenuItem>
                                </MenuItems>
                            </transition>
                        </Menu>
                    </div>
                    <div class="-mr-2 flex md:hidden">
                        <DisclosureButton
                            class="bg-gray-800 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                        >
                            <span class="sr-only">Open main menu</span>
                            <Bars3Icon
                                v-if="!open"
                                class="block h-6 w-6"
                                aria-hidden="true"
                            />
                            <XMarkIcon
                                v-else
                                class="block h-6 w-6"
                                aria-hidden="true"
                            />
                        </DisclosureButton>
                    </div>
                </div>
            </div>

            <DisclosurePanel class="md:hidden">
                <div
                    v-if="user != null"
                    class="px-2 pt-2 pb-3 space-y-1 sm:px-3"
                >
                    <router-link
                        v-for="item in navigation"
                        :key="item.name"
                        :to="item.to"
                        :class="[
                            item.active ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                            'block px-3 py-2 rounded-md text-base font-medium',
                        ]"
                        :aria-current="item.active ? 'page' : undefined"
                        @click="close()"
                    >
                        {{ item.name }}
                    </router-link>
                </div>
                <div
                    v-if="user != null"
                    class="pt-4 pb-3 border-t border-gray-700"
                >
                    <div class="flex items-center px-5">
                        <UserAvatar
                            :user="user"
                            class="h-10 w-10"
                        />
                        <div class="ml-3">
                            <div class="text-base font-medium leading-none text-white">{{ user.name }}</div>
                            <div class="text-sm font-medium leading-none text-gray-400">{{ user.email }}</div>
                        </div>
                    </div>
                    <div class="mt-3 px-2 space-y-1">
                        <a
                            href="#"
                            class="block px-3 py-2 rounded-md text-base font-medium text-gray-400 hover:text-white hover:bg-gray-700"
                            @click.prevent="signout"
                        >
                            Sign out
                        </a>
                    </div>
                </div>
                <div
                    v-else
                    class="px-2 pt-2 pb-3 space-y-1 sm:px-3"
                >
                    <router-link
                        to="/login"
                        class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-gray-700 hover:text-white"
                        @click="close()"
                    >
                        Sign in
                    </router-link>
                    <router-link
                        to="/signup"
                        class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-gray-700 hover:text-white"
                        @click="close()"
                    >
                        Create account
                    </router-link>
                </div>
            </DisclosurePanel>
        </Disclosure>

        <div
            v-if="showAlertBar"
            class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4"
        >
            <AlertBar
                type="dismissable"
                variant="failure"
                :title="alertBarModel.title"
                :message="alertBarModel.message"
                :details="alertBarModel.details"
                @close="showAlertBar = false"
            />
        </div>

        <main>
            <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                <router-view />
            </div>
        </main>

        <ErrorModal ref="errorModal" />
        <ConfirmModal ref="confirmModal" />
        <InformationModal ref="informationModal" />
    </div>
</template>

<script setup lang="ts">
import { inject, onMounted, computed, Ref, ref } from "vue";
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { Bars3Icon, XMarkIcon } from "@heroicons/vue/24/solid";
import { routes } from "./router";
import { useUserStore } from "./stores/user";
import { Modal, useModalStore } from "./stores/modals";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { axiosSymbol, routerSymbol } from "./symbols";
import AlertBar from "./components/AlertBar.vue";
import BrandMark from "./components/BrandMark.vue";
import UserAvatar from "./components/UserAvatar.vue";
import ErrorModal from "./components/ErrorModal.vue";
import ConfirmModal from "./components/ConfirmModal.vue";
import InformationModal from "./components/InformationModal.vue";
import { AxiosError, AxiosInstance } from "axios";
import useApi from "./api";
import { describeApiError } from "./utilities/apiErrors";

interface NavItem {
    name: string;
    to: string;
    active: boolean;
}

const $http = inject(axiosSymbol)!;
const $router = inject(routerSymbol)!;
const route = useRoute();

const { user } = storeToRefs(useUserStore());
const { fetchUser, logout } = useUserStore();
const { authApi } = useApi();

const modals = useModalStore();

const errorModal: Ref<Modal | null> = ref(null);
const confirmModal: Ref<Modal | null> = ref(null);
const informationModal: Ref<Modal | null> = ref(null);
const showAlertBar = ref(false);

const alertBarModel = ref({
    title: "",
    message: "",
    details: "",
});

const navigation = computed<NavItem[]>(() =>
    routes
        .filter((r) => r.meta?.title && !(r.meta?.hideOnNav ?? false) && !(r.meta?.disabledAfterLogin ?? false))
        .filter((r) => !r.meta?.requiresAdmin || (user.value?.isAdmin ?? false))
        .map((r) => ({
            name: r.meta!.title!,
            to: r.path,
            active: route.path == r.path,
        }))
);

const onRespError = (err: AxiosError): Promise<AxiosError> => {
    if (err.config?.silent || err.response?.status === 401 || err.response?.status === 402 || err.response?.status === 404) return Promise.reject(err);
    alertBarModel.value = describeApiError(err);
    showAlertBar.value = true;
    return Promise.reject(err);
};

function setupInterceptors(instance: AxiosInstance): AxiosInstance {
    instance.interceptors.response.use(config => config, onRespError);
    return instance;
}

async function signout() {
    await authApi.logout();
    logout();
    $router.push("/login");
}

onMounted(async () => {
    await fetchUser();
    setupInterceptors($http);
    modals.register("error", errorModal as Ref<Modal>);
    modals.register("confirm", confirmModal as Ref<Modal>);
    modals.register("information", informationModal as Ref<Modal>);
});
</script>
